import type { LogsEvent, LogsEventDomainContext } from '@datadog/browser-logs';

import { ChunkLoadErrorService } from '../service/ChunkLoadErrorService';

/**
 * Datadog Logs 전송 정책.
 *
 * Error Tracking 신호 품질을 떨어뜨리는 노이즈 로그를 source 단에서 차단하기 위한 판단 모음.
 * `@datadog/browser-logs`의 `beforeSend` 콜백에서 `shouldSendLog`를 호출해서 사용한다.
 */
export class DatadogLogPolicy {
  /**
   * 분석 불가능하거나 사용자 환경 기인 노이즈성 에러 메시지 패턴.
   * - 'Script error.' 포함 (대소문자 무시): cross-origin 스크립트에서 발생, 본문/스택 비공개라 fingerprint 불가.
   *   'Uncaught Script error.' 등 prefix가 붙은 변형도 동일 노이즈이므로 부분 일치로 잡는다.
   * - 청크 / 동적 import 로드 실패: 배포 사이 새로고침 미수행 시 발생, 비버그성 (정의는 ChunkLoadErrorService가 SoT)
   * - 'ResizeObserver loop ...': 브라우저가 한 프레임 안에 크기 변경 알림을 다 전달하지 못했을 때 내는
   *   경고성 메시지. 화면 동작에 영향이 없고 스택이 없어 추적 불가. 크롬의 'loop completed with
   *   undelivered notifications' 와 파이어폭스·구버전 크롬의 'loop limit exceeded', 'Uncaught' prefix
   *   변형을 부분 일치로 함께 잡는다.
   */
  static readonly NOISY_LOG_MESSAGE_PATTERNS: readonly (string | RegExp)[] = [
    /Script error\./i,
    /ResizeObserver loop/i,
    ...ChunkLoadErrorService.MESSAGE_PATTERNS,
  ];

  /** 네트워크 요청 취소 시 발생하는 error kind (axios, fetch AbortController) */
  static readonly ABORT_ERROR_KINDS: readonly string[] = [
    'AbortError',
    'CanceledError',
  ];

  /** 네트워크 요청 취소 시 발생하는 error message */
  static readonly ABORT_ERROR_MESSAGES: readonly string[] = [
    'canceled',
    'cancelled',
  ];

  /** HTTP 4xx 응답 상태 코드 범위 (사용자 환경 / 만료 토큰 등 비버그성) */
  static readonly HTTP_4XX_MIN = 400;
  static readonly HTTP_4XX_MAX = 499;

  /** Browser SDK가 네트워크 인스트루먼테이션으로 자동 수집한 로그를 식별하는 origin */
  static readonly NETWORK_ORIGIN = 'network';

  /** 개발자가 logger.error 등으로 명시 호출한 로그의 origin (메시지 노이즈 필터 제외 대상) */
  static readonly LOGGER_ORIGIN = 'logger';

  /**
   * 분석 불가능한 노이즈 메시지인지 판단한다.
   */
  static isNoisyMessage(message: string): boolean {
    return DatadogLogPolicy.NOISY_LOG_MESSAGE_PATTERNS.some((pattern) =>
      typeof pattern === 'string' ? message === pattern : pattern.test(message),
    );
  }

  /**
   * 청크 / 동적 모듈 로드 실패 error kind 인지 판단한다.
   * 배포 사이 새로고침 미수행 시 발생하는 비버그성 케이스로 노이즈로 분류한다.
   * 청크 정의는 ChunkLoadErrorService 를 SoT 로 재사용한다.
   */
  static isChunkLoadError(kind: string | undefined): boolean {
    if (!kind) return false;
    return ChunkLoadErrorService.KINDS.includes(kind);
  }

  /**
   * Browser SDK가 네트워크 인스트루먼테이션으로 자동 수집한 4xx 응답인지 판단한다.
   *
   * - `origin === 'network'` + `http.status_code` 가 400~499 일 때만 true
   * - 사용자가 코드에서 명시적으로 호출한 `logger.error('... status code 404 ...')` 는
   *   origin 이 `'logger'` 라서 false 가 반환되어 통과시킨다.
   */
  static isNetworkHttp4xx({
    origin,
    statusCode,
  }: {
    origin: string | undefined;
    statusCode: number | undefined;
  }): boolean {
    if (origin !== DatadogLogPolicy.NETWORK_ORIGIN) return false;
    if (statusCode === undefined) return false;
    return (
      statusCode >= DatadogLogPolicy.HTTP_4XX_MIN &&
      statusCode <= DatadogLogPolicy.HTTP_4XX_MAX
    );
  }

  /**
   * 요청 취소(abort)로 인한 에러인지 판단한다.
   * 사용자가 페이지를 떠나 요청이 cancel 된 정상 종료 케이스.
   */
  static isAbortError({
    kind,
    message,
  }: {
    kind: string | undefined;
    message: string | undefined;
  }): boolean {
    if (kind && DatadogLogPolicy.ABORT_ERROR_KINDS.includes(kind)) {
      return true;
    }
    if (message && DatadogLogPolicy.ABORT_ERROR_MESSAGES.includes(message)) {
      return true;
    }
    return false;
  }

  /**
   * Browser SDK 네트워크 인스트루먼테이션이 abort 로 감지한 요청인지 판단한다.
   *
   * `NetworkLogsEventDomainContext.isAborted` 는 SDK 가 fetch/XHR 단계에서 직접 set 하는
   * 권위 있는 신호이므로 `error.kind`/`message` 패턴 매칭(axios/fetch 라이브러리 버전에 의존)보다 안정적이다.
   */
  static isAbortedNetworkRequest({
    origin,
    context,
  }: {
    origin: string | undefined;
    context: LogsEventDomainContext | undefined;
  }): boolean {
    if (origin !== DatadogLogPolicy.NETWORK_ORIGIN) return false;
    if (!context) return false;
    return 'isAborted' in context && context.isAborted === true;
  }

  /**
   * Datadog Logs `beforeSend` 콜백 본체.
   *
   * SDK 시그니처: `(event, context) => boolean`. context 는 origin 별로 다른 도메인 정보를 담는다.
   * 네트워크 요청 abort 는 `context.isAborted` 로, 그 외 라이브러리 단의 cancel 은
   * `error.kind`/`message` 로 검사한다 (이중 안전망).
   *
   * @returns `false` 면 drop, `true` 면 Datadog 로 전송
   */
  static shouldSendLog({
    event,
    context,
  }: {
    event: Pick<LogsEvent, 'message' | 'error' | 'origin' | 'http'>;
    context?: LogsEventDomainContext;
  }): boolean {
    const { message, error, origin, http } = event;

    // 개발자가 logger.error 등으로 명시 호출한 로그(origin 'logger')는
    // 메시지에 노이즈 패턴이 포함돼도 의도된 로그이므로 필터에서 제외한다.
    if (
      origin !== DatadogLogPolicy.LOGGER_ORIGIN &&
      typeof message === 'string' &&
      DatadogLogPolicy.isNoisyMessage(message)
    ) {
      return false;
    }

    if (DatadogLogPolicy.isChunkLoadError(error?.kind)) {
      return false;
    }

    if (
      DatadogLogPolicy.isNetworkHttp4xx({
        origin,
        statusCode: http?.status_code,
      })
    ) {
      return false;
    }

    if (DatadogLogPolicy.isAbortedNetworkRequest({ origin, context })) {
      return false;
    }

    if (
      DatadogLogPolicy.isAbortError({
        kind: error?.kind,
        message: error?.message,
      })
    ) {
      return false;
    }

    return true;
  }
}
